import React, { useEffect, useState } from "react";
import { z } from "zod";
import luhn from "luhn";
import { isBefore, isValid, parse, startOfMonth } from "date-fns";
import { Input } from "@picklebet/ui";
import { Form } from "@picklebet/ui";
import { getCreditCardType } from "utilities/helpers";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useHookFormMask } from "use-mask-input";
import { Button } from "@picklebet/ui";
import { AddCardFormSkeleton } from "./AddCardFormSkeleton";
import { LEGAL_NAME } from "utilities/regex";
import loadjs from "loadjs";
import * as styles from "./AddCardForm.module.scss";

const today = startOfMonth(new Date());

const schema = z.object({
  name: z
    .string({ error: "Name should not be empty" })
    .min(2, "Name should be at least 2 characters long")
    .regex(LEGAL_NAME, "Please enter a valid name"),
  number: z
    .string({ error: "Card Number should not be empty" })
    .superRefine((value, ctx) => {
      const cardNumberIsValid = luhn.validate(value);
      const cardType = getCreditCardType(value);

      if (!cardNumberIsValid) {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a valid Card Number",
        });
        return;
      }
      if (!["visa", "mastercard"].includes(cardType)) {
        ctx.addIssue({
          code: "custom",
          message: "Only valid Visa or Mastercard cards are accepted",
        });
      }
    }),
  expiry: z
    .string({ error: "Expiry date should not be empty" })
    .superRefine((value, ctx) => {
      const formattedDate = parse(value, "MM/yy", today);
      const date = isValid(formattedDate)
        ? formattedDate
        : new Date(1999, 1, 1);

      if (isBefore(date, today)) {
        ctx.addIssue({
          code: "custom",
          message: "Expiry must be a complete and valid date in the future",
        });
      }
    }),
});

export type AddCardFormValues = z.infer<typeof schema>;

type AddCardFormProps = {
  onSubmit: (values: AddCardFormValues) => void;
};

const AddCardForm = ({ onSubmit }: AddCardFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      number: "",
      expiry: "",
    },
    resolver: zodResolver(schema),
  });

  const [mwScriptLoaded, setMwScriptLoaded] = useState(loadjs.isDefined("mw"));

  useEffect(() => {
    if (mwScriptLoaded || loadjs.isDefined("mw")) return;

    loadjs(process.env.GATSBY_MERCHANTWARRIOR_SDK_URL as string, "mw", () =>
      setMwScriptLoaded(true),
    );
  }, []);

  const registerWithMask = useHookFormMask(register);

  if (!mwScriptLoaded) {
    return <AddCardFormSkeleton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noSpacing className={styles.form}>
      <Input
        label="Full Name"
        errorMessage={errors?.name?.message}
        noSpacing
        {...register("name")}
      />

      <Input
        label="Card Number"
        inputMode="numeric"
        errorMessage={errors?.number?.message}
        noSpacing
        {...registerWithMask("number", "9999 9999 9999 9999", {
          placeholder: "",
          autoUnmask: true,
        })}
      />

      <Input
        label="Expiry Date"
        errorMessage={errors?.expiry?.message}
        inputMode="numeric"
        placeholder="MM/YY"
        noSpacing
        {...registerWithMask("expiry", "99/99", {
          placeholder: "",
          showMaskOnHover: false,
          showMaskOnFocus: false,
        })}
      />

      <Button loading={isSubmitting} type="submit">
        Add Card
      </Button>
    </Form>
  );
};

export { AddCardForm };
